import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workItemApi } from '../services/softwareFactoryApi';
import type { ListResponse, ListWorkItemsParams, SfWorkItem, SfWorkItemCreate, SfWorkItemUpdate, WorkItemTransitionData } from '../types';

export const workItemKeys = {
  all: ['sdevWorkItems'] as const,
  list: (params?: ListWorkItemsParams) => [...workItemKeys.all, 'list', params] as const,
  detail: (id: string) => [...workItemKeys.all, 'detail', id] as const,
  transitions: (id: string) => [...workItemKeys.all, 'transitions', id] as const,
  commits: (id: string) => [...workItemKeys.all, 'commits', id] as const,
  customers: (id: string) => [...workItemKeys.all, 'customers', id] as const,
  children: (id: string) => [...workItemKeys.all, 'children', id] as const,
  dispatchable: ['sdevWorkItems', 'dispatchable'] as const,
};

export function useWorkItemChildren(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: workItemKeys.children(id),
    queryFn: () => workItemApi.listChildren(id),
    enabled: !!id && (options?.enabled ?? true),
  });
}

export function useWorkItems(params?: ListWorkItemsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: workItemKeys.list(params),
    queryFn: () => workItemApi.list(params),
    enabled: options?.enabled ?? true,
  });
}

export function useWorkItem(id: string) {
  return useQuery({
    queryKey: workItemKeys.detail(id),
    queryFn: () => workItemApi.get(id),
    enabled: !!id,
  });
}

export function useCreateWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SfWorkItemCreate) => workItemApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
    },
  });
}

export function useUpdateWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SfWorkItemUpdate }) => workItemApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
      // A sprint_id change moves the item between the backlog and a sprint board,
      // so refresh the sprint caches too (mirrors useAssignWorkItemToSprint). Keyed
      // by the sprintKeys.all prefix, inlined to avoid a circular hook-module import.
      if (variables.data.sprint_id !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['sdevSprints'] });
      }
    },
  });
}

/**
 * Reschedule a work item's planned span (start_date / end_date) with an
 * optimistic patch across every cached work-item list, rolling back on error.
 * Used by the Roadmap Gantt's drag-to-reschedule interaction — the optimistic
 * update lets the bar settle at its new position before the server confirms.
 * Dates are ISO `yyyy-MM-dd` strings (the API's planning-date format).
 */
export function useRescheduleWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, start_date, end_date }: { id: string; start_date: string; end_date: string }) =>
      workItemApi.update(id, { start_date, end_date }),
    onMutate: async ({ id, start_date, end_date }) => {
      await queryClient.cancelQueries({ queryKey: workItemKeys.all });
      const snapshots = queryClient.getQueriesData<ListResponse<SfWorkItem>>({ queryKey: workItemKeys.all });
      for (const [key, data] of snapshots) {
        if (!data?.items) continue;
        queryClient.setQueryData<ListResponse<SfWorkItem>>(key, {
          ...data,
          items: data.items.map((w) => (w.id === id ? { ...w, start_date, end_date } : w)),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
      // The global mutation-error handler shows the specific translated toast.
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
    },
  });
}

export function useDeleteWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workItemApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
    },
  });
}

export function useUpdateWorkItemStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => workItemApi.updateStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
    },
  });
}

export function useRebaselineWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workItemApi.rebaseline(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(id) });
    },
  });
}

export function useWorkItemTransitions(id: string) {
  return useQuery({
    queryKey: workItemKeys.transitions(id),
    queryFn: () => workItemApi.listTransitions(id),
    enabled: !!id,
  });
}

export function useTransitionWorkItemStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WorkItemTransitionData }) =>
      workItemApi.transitionStatus(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: workItemKeys.transitions(variables.id) });
    },
  });
}

export function useLinkedTicket(workItemId: string) {
  return useQuery({
    queryKey: [...workItemKeys.all, 'linked-ticket', workItemId] as const,
    queryFn: () => workItemApi.getLinkedTicket(workItemId),
    enabled: !!workItemId,
  });
}

export function useWorkItemCommits(id: string) {
  return useQuery({
    queryKey: workItemKeys.commits(id),
    queryFn: () => workItemApi.getCommits(id),
    enabled: !!id,
  });
}

export function useLinkCommits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shas }: { id: string; shas: string[] }) => workItemApi.linkCommits(id, shas),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.commits(variables.id) });
    },
  });
}

export function useVoteOnWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workItemApi.vote(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.customers(id) });
    },
  });
}

export function useWorkItemCustomers(id: string) {
  return useQuery({
    queryKey: workItemKeys.customers(id),
    queryFn: () => workItemApi.listCustomers(id),
    enabled: !!id,
  });
}

export function useAddWorkItemCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      organization_id,
      contact_id,
      deal_id,
    }: {
      id: string;
      organization_id?: string | null;
      contact_id?: string | null;
      deal_id?: string | null;
    }) => workItemApi.addCustomer(id, { organization_id, contact_id, deal_id }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.customers(variables.id) });
    },
  });
}

export function useRemoveWorkItemCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, customerId }: { id: string; customerId: string }) =>
      workItemApi.removeCustomer(id, customerId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.customers(variables.id) });
    },
  });
}
